import { ID } from '@griply/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarColorBar } from 'shared/components/ui/calendar-color-bar';
import {
  CustomFormSelect,
  Option,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';

import * as Styled from './calendar-sync-options.style';

export type CalendarSelectValue = { connectionId: ID; calendarId: ID };

const toOptionValue = ({ connectionId, id }: ConnectionCalendar) =>
  `${connectionId}:${id}`;

export type CalendarOptionValueProps = { value: Option };

const CalendarOptionValue: React.FC<CalendarOptionValueProps> = ({ value }) => (
  <Styled.SelectValue>
    {value.start}
    <Styled.SelectLabel>{value.label}</Styled.SelectLabel>
  </Styled.SelectValue>
);

export type CalendarSelectProps = {
  // Every known calendar — resolves the current value even when its connection can no longer sync.
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  value: CalendarSelectValue | null;
  onChange: (value: CalendarSelectValue) => void;
};

export const CalendarSelect: React.FC<CalendarSelectProps> = ({
  calendars,
  selectableCalendars,
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const selected = useMemo(
    () =>
      calendars.find(
        (calendar) =>
          calendar.id === value?.calendarId &&
          calendar.connectionId === value?.connectionId,
      ),
    [calendars, value],
  );

  const options = useMemo(
    () =>
      selectableCalendars.map((calendar) => ({
        value: toOptionValue(calendar),
        label: calendar.name,
        start: <CalendarColorBar color={calendar.color} />,
      })),
    [selectableCalendars],
  );

  const onSelect = (optionValue?: string) => {
    const calendar = selectableCalendars.find(
      (calendar) => toOptionValue(calendar) === optionValue,
    );
    if (!calendar) {
      return;
    }

    onChange({ connectionId: calendar.connectionId, calendarId: calendar.id });
  };

  const selectedValue = selected ? toOptionValue(selected) : undefined;
  // A calendar on a connection that can no longer sync is not in the options,
  // so its name is passed straight to the trigger to keep the value visible.
  const unselectableLabel = options.some(
    (option) => option.value === selectedValue,
  )
    ? undefined
    : selected?.name;

  return (
    <CustomFormSelect
      options={options}
      value={selectedValue}
      selectedLabel={unselectableLabel}
      renderValue={CalendarOptionValue}
      placeholder={t(
        'forms.user-settings.sections.calendar.sync-tasks.placeholder',
      )}
      onChange={onSelect}
      size={SelectSize.ExtraSmall}
      noOverflow
    />
  );
};
